import React, { useState, useEffect } from 'react';
import {
  Edit,
  Save,
  X,
  Plus,
  Trash2,
  AlertTriangle,
  Building2,
  Upload,
  Loader,
  MapPin
} from 'lucide-react';
import { trackAccountManagement, trackError } from '../Utils/analytics';
import { getCurrentLocationDetails } from '../Utils/location';
import './UserKitchenManager.css'

interface Kitchen {
  id: number;
  kitchen_name: string;
  kitchen_type: string;
  contact_number: string;
  kitchen_address: string;
  state: string;
  city: string;
  country: string;
  pincode: string;
  kitchen_category: string;
  made_by: string;
  kitchen_photo?: string;
  status?: boolean;
  is_online?: boolean;
  status_display?: string;
  raw_kitchen_data?: any;
}

interface UserKitchenManagerProps {
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

const UserKitchenManager: React.FC<UserKitchenManagerProps> = ({ showNotification }) => {
  const [kitchen, setKitchen] = useState<Kitchen | null>(null);
  const [isEditingKitchen, setIsEditingKitchen] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [isDeletingKitchen, setIsDeletingKitchen] = useState(false);
  const [showDeleteKitchenConfirmation, setShowDeleteKitchenConfirmation] = useState(false);
  const [isCreatingKitchen, setIsCreatingKitchen] = useState(false);
  const [isDetectingKitchenLocation, setIsDetectingKitchenLocation] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Kitchen form state
  const [kitchenFormData, setKitchenFormData] = useState({
    kitchen_name: '',
    kitchen_type: '',
    contact_number: '',
    kitchen_address: '',
    state: '',
    city: '',
    country: '', // This will store country name for display
    country_code: '', // This will store country code for backend
    pincode: '',
    kitchen_category: '',
    made_by: '',
    kitchen_photo: null as File | null
  });
  const [kitchenFormErrors, setKitchenFormErrors] = useState<{[key: string]: string}>({});
  const [isSubmittingKitchen, setIsSubmittingKitchen] = useState(false);
  const [kitchenImagePreview, setKitchenImagePreview] = useState<string | null>(null);
  const [isLocationDetected, setIsLocationDetected] = useState(false);

  // Auto-detect location when component mounts
  useEffect(() => {
    if (!isLocationDetected) {
      handleDetectKitchenLocation();
    }
  }, []);

  // Debug kitchen state changes
  useEffect(() => {
    console.log('🔍 Kitchen state changed:', kitchen);
  }, [kitchen]);

  // Listen for kitchen status changes from other components
  useEffect(() => {
    const handleKitchenStatusUpdate = (event: any) => {
      console.log('Received kitchen status update event:', event.detail);
      
      if (event.detail.kitchenId === kitchen?.id && event.detail.kitchen) {
        console.log('🔄 Updating kitchen from event:', event.detail.kitchen);
        setKitchen(event.detail.kitchen);
      } else if (event.detail.kitchenId === kitchen?.id && typeof event.detail.isOnline === 'boolean') {
        console.log('🔄 Updating kitchen status from event:', event.detail.isOnline);
        setKitchen(prev => prev ? {
          ...prev,
          is_online: event.detail.isOnline
        } : null);
      }
    };

    window.addEventListener('kitchenStatusChanged', handleKitchenStatusUpdate);
    
    return () => {
      window.removeEventListener('kitchenStatusChanged', handleKitchenStatusUpdate);
    };
  }, [kitchen]);

  const fetchKitchenData = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        setError('Authentication required. Please log in again.');
        setIsLoading(false);
        return;
      }

      const response = await fetch('https://api.homecookt.com/api/v1/my-kitchen', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const kitchenData = await response.json();
        console.log('📥 BACKEND DATA FETCHED:', kitchenData);
        
        // Only update if we're not in the middle of an optimistic update
        if (!isTogglingStatus && !isCreatingKitchen && !isDeletingKitchen) {
          console.log('✅ SETTING BACKEND DATA');
          setKitchen(kitchenData);
        } else {
          console.log('🔄 OPERATION IN PROGRESS - keeping current state');
          
          // During operations, don't override the current state
          console.log('💾 KEEPING CURRENT STATE DURING OPERATION');
        }
      } else if (response.status === 404) {
        console.log('❌ NO KITCHEN - 404 response from backend');
        setKitchen(null);
      } else {
        console.error('❌ KITCHEN FETCH ERROR:', response.status);
        setError('Failed to load kitchen data');
        trackError('kitchen_fetch_failed', 'user_profile');
      }
    } catch (error) {
      console.error('❌ KITCHEN FETCH NETWORK ERROR:', error);
      setError('Network error. Please check your connection.');
      trackError('kitchen_fetch_network_error', 'user_profile');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    
    if (!authToken) {
      setError('Authentication required. Please log in again.');
      setIsLoading(false);
      return;
    }

    fetchKitchenData();
  }, []);

  // Handle kitchen status toggle
  const handleToggleKitchenStatus = async () => {
    if (!kitchen || isTogglingStatus) return;

    const newStatus = !kitchen.status;
    console.log(`Toggling kitchen status: ${kitchen.status} -> ${newStatus ? 'online' : 'offline'}`);

    setIsTogglingStatus(true);

    // Optimistic update
    const optimisticKitchen = {
      ...kitchen,
      status: newStatus,
      is_online: newStatus,
      status_display: newStatus ? 'ONLINE' : 'OFFLINE'
    };
    setKitchen(optimisticKitchen);

    try {
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(`https://api.homecookt.com/api/v1/${kitchen.id}/status?status=${newStatus}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        console.log('Kitchen status updated successfully:', data);
        
        // Show status notification
        showNotification(
          'success',
          `Kitchen ${newStatus ? 'Online' : 'Offline'}`,
          newStatus 
            ? 'Your kitchen is now open and ready to receive orders!' 
            : 'Your kitchen is now closed and not accepting new orders.'
        );
        
        // Wait a bit for backend to process, then fetch updated data
        setTimeout(() => {
          fetchKitchenData();
        }, 2000);
        
        // Dispatch event for other components
        window.dispatchEvent(new CustomEvent('kitchenStatusChanged', { 
          detail: { kitchenId: kitchen.id, status: newStatus } 
        }));
        
        trackAccountManagement('kitchen_updated');
      } else {
        console.error('Kitchen status toggle failed:', response.status, data);
        // Revert optimistic update
        setKitchen(kitchen);
        setError(data.message || 'Failed to update kitchen status');
        trackError('kitchen_status_toggle_failed', 'user_profile');
      }
    } catch (error) {
      console.error('Kitchen status toggle error:', error);
      // Revert optimistic update
      setKitchen(kitchen);
      setError('Network error. Please try again.');
      trackError('kitchen_status_toggle_network_error', 'user_profile');
    } finally {
      setIsTogglingStatus(false);
    }
  };

  // Handle edit kitchen
  const handleEditKitchen = () => {
    if (!kitchen) return;
    
    // Pre-populate form with current kitchen data
    setKitchenFormData({
      kitchen_name: kitchen.kitchen_name || '',
      kitchen_type: kitchen.kitchen_type || '',
      contact_number: kitchen.contact_number || '',
      kitchen_address: kitchen.kitchen_address || '',
      state: kitchen.state || '',
      city: kitchen.city || '',
      country: kitchen.country || '',
      country_code: kitchen.country || '', // Assuming backend stores country name, we'll detect code
      pincode: kitchen.pincode || '',
      kitchen_category: kitchen.kitchen_category || '',
      made_by: kitchen.made_by || '',
      kitchen_photo: null
    });
    
    // Auto-detect location to get country code if not already detected
    if (!isLocationDetected) {
      handleDetectKitchenLocation();
    }
    
    // Set current image preview if exists
    if (kitchen.kitchen_photo) {
      setKitchenImagePreview(kitchen.kitchen_photo);
    }
    
    setIsEditingKitchen(true);
    setKitchenFormErrors({});
  };

  // Handle kitchen form input changes
  const handleKitchenFormChange = (field: string, value: string) => {
    setKitchenFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (kitchenFormErrors[field]) {
      setKitchenFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle kitchen image upload
  const handleKitchenImageUpload = (file: File) => {
    setKitchenFormData(prev => ({
      ...prev,
      kitchen_photo: file
    }));
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setKitchenImagePreview(previewUrl);
  };

  // Handle automatic location detection for kitchen address
  const handleDetectKitchenLocation = async () => {
    setIsDetectingKitchenLocation(true);
    
    // Clear any existing address errors
    const addressFields = ['kitchen_address', 'city', 'pincode'];
    setKitchenFormErrors(prev => {
      const newErrors = { ...prev };
      addressFields.forEach(field => delete newErrors[field]);
      return newErrors;
    });

    try {
      const result = await getCurrentLocationDetails();
      
      if ('type' in result) {
        // Handle error
        setKitchenFormErrors(prev => ({
          ...prev,
          kitchen_address: result.message
        }));
      } else {
        // Success - update address fields (country and state are auto-filled and non-editable)
        setKitchenFormData(prev => ({
          ...prev,
          state: result.state,
          country: result.country_name,
          country_code: result.country_code
        }));
        
        setIsLocationDetected(true);
        
        console.log('Kitchen location detected:', {
          kitchen_address: result.full_address,
          city: result.city,
          state: result.state,
          country: result.country_name,
          country_code: result.country_code,
          pincode: result.postal_code
        });
      }
    } catch (error) {
      console.error('Kitchen location detection failed:', error);
      setKitchenFormErrors(prev => ({
        ...prev,
        kitchen_address: 'Failed to detect location. Please allow location access and try again.'
      }));
    } finally {
      setIsDetectingKitchenLocation(false);
    }
  };

  // Validate kitchen form
  const validateKitchenForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!kitchenFormData.kitchen_name.trim()) newErrors.kitchen_name = 'Kitchen name is required';
    if (!kitchenFormData.kitchen_type.trim()) newErrors.kitchen_type = 'Kitchen type is required';
    if (!kitchenFormData.contact_number.trim()) newErrors.contact_number = 'Contact number is required';
    if (!kitchenFormData.kitchen_address.trim()) newErrors.kitchen_address = 'Kitchen address is required';
    if (!kitchenFormData.city.trim()) newErrors.city = 'City is required';
    if (!kitchenFormData.country.trim()) newErrors.country = 'Country detection failed. Please refresh and allow location access.';
    if (!kitchenFormData.country_code.trim()) newErrors.country_code = 'Country code is required for registration.';
    if (!kitchenFormData.state.trim()) newErrors.state = 'State detection failed. Please refresh and allow location access.';
    if (!kitchenFormData.pincode.trim()) newErrors.pincode = 'Pincode is required';
    if (!kitchenFormData.kitchen_category.trim()) newErrors.kitchen_category = 'Kitchen category is required';
    if (!kitchenFormData.made_by.trim()) newErrors.made_by = 'Made by is required';

    // Phone number validation
    if (kitchenFormData.contact_number && !/^[0-9]{10}$/.test(kitchenFormData.contact_number.replace(/\s/g, ''))) {
      newErrors.contact_number = 'Please enter a valid 10-digit phone number';
    }

    // Pincode validation
    if (kitchenFormData.pincode && !/^[0-9]{6}$/.test(kitchenFormData.pincode)) {
      newErrors.pincode = 'Please enter a valid 6-digit pincode';
    }

    setKitchenFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle kitchen form submission
  const handleKitchenFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateKitchenForm() || !kitchen) {
      trackError('kitchen_edit_validation_failed', 'user_profile');
      return;
    }

    setIsSubmittingKitchen(true);
    setKitchenFormErrors({});

    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        setKitchenFormErrors({ general: 'Authentication required. Please log in again.' });
        setIsSubmittingKitchen(false);
        return;
      }

      const formData = new FormData();
      
      // Add all required fields
      formData.append('kitchen_name', kitchenFormData.kitchen_name.trim());
      formData.append('kitchen_type', kitchenFormData.kitchen_type.trim());
      formData.append('contact_number', kitchenFormData.contact_number.replace(/\s/g, ''));
      formData.append('kitchen_address', kitchenFormData.kitchen_address.trim());
      formData.append('state', kitchenFormData.state.trim());
      formData.append('city', kitchenFormData.city.trim());
      formData.append('country', kitchenFormData.country_code.trim()); // Send country code to backend
      formData.append('pincode', kitchenFormData.pincode.trim());
      formData.append('kitchen_category', kitchenFormData.kitchen_category.trim());
      formData.append('made_by', kitchenFormData.made_by.trim());
      
      // Add photo if selected
      if (kitchenFormData.kitchen_photo) {
        formData.append('kitchen_photo', kitchenFormData.kitchen_photo);
      }

      const response = await fetch(`https://api.homecookt.com/api/v1/updatekitchen/${kitchen.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        console.log('Kitchen updated successfully:', data);
        
        // Update kitchen state with new data
        setKitchen(prev => ({
          ...prev,
          ...data,
          status: prev?.status, // Preserve current status
          is_online: prev?.is_online,
          status_display: prev?.status_display
        }));
        
        setIsEditingKitchen(false);
        setKitchenImagePreview(null);
        trackAccountManagement('kitchen_updated');
        
        // Refresh kitchen data from backend
        setTimeout(() => {
          fetchKitchenData();
        }, 1000);
        
      } else {
        let errorMessage = 'Failed to update kitchen';
        
        switch (response.status) {
          case 400:
            errorMessage = data.message || 'Invalid data provided. Please check all fields.';
            break;
          case 404:
            errorMessage = 'Kitchen not found. Please refresh the page.';
            break;
          case 403:
            errorMessage = 'You do not have permission to update this kitchen.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data.message || `Error ${response.status}: Please try again.`;
        }
        
        setKitchenFormErrors({ general: errorMessage });
        trackError('kitchen_update_api_failed', 'user_profile');
      }
    } catch (error) {
      console.error('Kitchen update error:', error);
      setKitchenFormErrors({ general: 'Network error. Please check your connection and try again.' });
      trackError('kitchen_update_network_error', 'user_profile');
    } finally {
      setIsSubmittingKitchen(false);
    }
  };

  // Handle cancel kitchen edit
  const handleCancelKitchenEdit = () => {
    setIsEditingKitchen(false);
    setKitchenFormData({
      kitchen_name: '',
      kitchen_type: '',
      contact_number: '',
      kitchen_address: '',
      state: '',
      city: '',
      country: '',
      country_code: '',
      pincode: '',
      kitchen_category: '',
      made_by: '',
      kitchen_photo: null
    });
    setKitchenFormErrors({});
    setKitchenImagePreview(null);
    setIsLocationDetected(false);
  };

  // Handle create new kitchen
  const handleCreateKitchen = () => {
    // Reset form data
    setKitchenFormData({
      kitchen_name: '',
      kitchen_type: '',
      contact_number: '',
      kitchen_address: '',
      state: '',
      city: '',
      country: '',
      country_code: '',
      pincode: '',
      kitchen_category: '',
      made_by: '',
      kitchen_photo: null
    });
    setKitchenFormErrors({});
    setKitchenImagePreview(null);
    setIsLocationDetected(false);
    setIsCreatingKitchen(true);
  };

  // Handle create kitchen form submission
  const handleCreateKitchenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateKitchenForm()) {
      trackError('kitchen_create_validation_failed', 'user_profile');
      return;
    }

    setIsSubmittingKitchen(true);
    setKitchenFormErrors({});

    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        setKitchenFormErrors({ general: 'Authentication required. Please log in again.' });
        setIsSubmittingKitchen(false);
        return;
      }

      const formData = new FormData();
      
      // Add all required fields
      formData.append('kitchen_name', kitchenFormData.kitchen_name.trim());
      formData.append('kitchen_type', kitchenFormData.kitchen_type.trim());
      formData.append('contact_number', kitchenFormData.contact_number.replace(/\s/g, ''));
      formData.append('kitchen_address', kitchenFormData.kitchen_address.trim());
      formData.append('state', kitchenFormData.state.trim());
      formData.append('city', kitchenFormData.city.trim());
      formData.append('country', kitchenFormData.country_code.trim()); // Send country code to backend
      formData.append('pincode', kitchenFormData.pincode.trim());
      formData.append('kitchen_category', kitchenFormData.kitchen_category.trim());
      formData.append('made_by', kitchenFormData.made_by.trim());
      
      // Add photo if selected
      if (kitchenFormData.kitchen_photo) {
        formData.append('kitchen_photo', kitchenFormData.kitchen_photo);
      }

      console.log('Creating new kitchen...');

      const response = await fetch('https://api.homecookt.com/api/v1/createkitchen', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        console.log('Kitchen created successfully:', data);
        
        // Set the new kitchen data
        const newKitchen = {
          ...data,
          status: false,
          is_online: false,
          status_display: 'OFFLINE'
        };
        
        setKitchen(newKitchen);
        
        setIsCreatingKitchen(false);
        setKitchenImagePreview(null);
        
        // Show success notification
        showNotification('success', 'Kitchen Created!', 'Your kitchen has been created successfully.');
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('kitchenCreated', {
          detail: { kitchen: newKitchen }
        }));
        
        trackAccountManagement('kitchen_created');
        
        // Refresh kitchen data from backend
        setTimeout(() => {
          fetchKitchenData();
        }, 1000);
        
      } else {
        let errorMessage = 'Failed to create kitchen';
        
        switch (response.status) {
          case 400:
            errorMessage = data.message || 'Invalid data provided. Please check all fields.';
            break;
          case 409:
            errorMessage = 'You already have a kitchen. Please refresh the page.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data.message || `Error ${response.status}: Please try again.`;
        }
        
        setKitchenFormErrors({ general: errorMessage });
        trackError('kitchen_create_api_failed', 'user_profile');
      }
    } catch (error) {
      console.error('Kitchen creation error:', error);
      setKitchenFormErrors({ general: 'Network error. Please check your connection and try again.' });
      trackError('kitchen_create_network_error', 'user_profile');
    } finally {
      setIsSubmittingKitchen(false);
    }
  };

  // Handle cancel kitchen creation
  const handleCancelKitchenCreate = () => {
    setIsCreatingKitchen(false);
    setKitchenFormData({
      kitchen_name: '',
      kitchen_type: '',
      contact_number: '',
      kitchen_address: '',
      state: '',
      city: '',
      country: '',
      country_code: '',
      pincode: '',
      kitchen_category: '',
      made_by: '',
      kitchen_photo: null
    });
    setKitchenFormErrors({});
    setKitchenImagePreview(null);
    setIsLocationDetected(false);
  };

  // Handle kitchen deletion
  const handleDeleteKitchen = async () => {
    if (!kitchen || isDeletingKitchen) return;

    setIsDeletingKitchen(true);

    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        showNotification('error', 'Authentication Error', 'Please log in again.');
        setIsDeletingKitchen(false);
        return;
      }

      console.log('Deleting kitchen with ID:', kitchen.id);

      const response = await fetch(`https://api.homecookt.com/api/v1/deletekitchen/${kitchen.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Kitchen deleted successfully:', data);
        
        // Clear kitchen state
        setKitchen(null);
        setShowDeleteKitchenConfirmation(false);
        
        // Show success notification
        showNotification('success', 'Kitchen Deleted!', 'Your kitchen and all associated data have been removed successfully.');
        
        // Dispatch event for other components to refresh
        window.dispatchEvent(new CustomEvent('kitchenDeleted', { 
          detail: { kitchenId: kitchen.id } 
        }));
        
        trackAccountManagement('kitchen_deleted');
        
      } else {
        const data = await response.json();
        let errorMessage = 'Failed to delete kitchen';
        
        switch (response.status) {
          case 404:
            errorMessage = 'Kitchen not found. It may have already been deleted.';
            // If kitchen not found, clear it from state anyway
            setKitchen(null);
            break;
          case 403:
            errorMessage = 'You do not have permission to delete this kitchen.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data.message || `Error ${response.status}: Please try again.`;
        }
        
        // Show error notification
        showNotification('error', 'Deletion Failed', errorMessage);
        trackError('kitchen_deletion_api_failed', 'user_profile');
      }
    } catch (error) {
      console.error('Kitchen deletion error:', error);
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
      trackError('kitchen_deletion_network_error', 'user_profile');
    } finally {
      setIsDeletingKitchen(false);
      setShowDeleteKitchenConfirmation(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="user-profile-loading">
        <div className="user-profile-loading-spinner"></div>
        <h3>Loading kitchen data...</h3>
        <p>Please wait while we fetch your kitchen information</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="user-profile-error">
        <h3>Unable to Load Kitchen</h3>
        <p>{error}</p>
        <div className="user-profile-error-actions">
          <button onClick={() => {
            setError(null);
            setIsLoading(true);
            fetchKitchenData();
          }} className="user-profile-retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Kitchen creation form
  if (isCreatingKitchen) {
    return (
      <div className="user-profile-kitchen-form">
        <div className="user-profile-kitchen-form-header">
          <h4>Create Kitchen</h4>
          <p>Set up your kitchen to start selling food</p>
        </div>

        <form onSubmit={handleCreateKitchenSubmit}>
          {/* Basic Information */}
          <div className="user-profile-form-section">
            <h5>Basic Information</h5>
            <div className="user-profile-form-row">
              <div className="user-profile-form-group">
                <label htmlFor="kitchen_name">Kitchen Name *</label>
                <input
                  id="kitchen_name"
                  type="text"
                  value={kitchenFormData.kitchen_name}
                  onChange={(e) => handleKitchenFormChange('kitchen_name', e.target.value)}
                  className={kitchenFormErrors.kitchen_name ? 'error' : ''}
                  placeholder="Enter kitchen name"
                />
                {kitchenFormErrors.kitchen_name && <div className="error-message">{kitchenFormErrors.kitchen_name}</div>}
              </div>

              <div className="user-profile-form-group">
                <label htmlFor="kitchen_type">Kitchen Type *</label>
                <select
                  id="kitchen_type"
                  value={kitchenFormData.kitchen_type}
                  onChange={(e) => handleKitchenFormChange('kitchen_type', e.target.value)}
                  className={kitchenFormErrors.kitchen_type ? 'error' : ''}
                >
                  <option value="">Select type</option>
                  <option value="Home Kitchen">Home Kitchen</option>
                  <option value="Cloud Kitchen">Cloud Kitchen</option>
                  <option value="Restaurant">Restaurant</option>
                </select>
                {kitchenFormErrors.kitchen_type && <div className="error-message">{kitchenFormErrors.kitchen_type}</div>}
              </div>
            </div>

            <div className="user-profile-form-row">
              <div className="user-profile-form-group">
                <label htmlFor="contact_number">Contact Number *</label>
                <input
                  id="contact_number"
                  type="tel"
                  value={kitchenFormData.contact_number}
                  onChange={(e) => handleKitchenFormChange('contact_number', e.target.value)}
                  className={kitchenFormErrors.contact_number ? 'error' : ''}
                  placeholder="10-digit phone number"
                  maxLength={10}
                />
                {kitchenFormErrors.contact_number && <div className="error-message">{kitchenFormErrors.contact_number}</div>}
              </div>

              <div className="user-profile-form-group">
                <label htmlFor="made_by">Made By *</label>
                <input
                  id="made_by"
                  type="text"
                  value={kitchenFormData.made_by}
                  onChange={(e) => handleKitchenFormChange('made_by', e.target.value)}
                  className={kitchenFormErrors.made_by ? 'error' : ''}
                  placeholder="Chef/Cook name"
                />
                {kitchenFormErrors.made_by && <div className="error-message">{kitchenFormErrors.made_by}</div>}
              </div>
            </div>

            <div className="user-profile-form-group">
              <label htmlFor="kitchen_category">Kitchen Category *</label>
              <select
                id="kitchen_category"
                value={kitchenFormData.kitchen_category}
                onChange={(e) => handleKitchenFormChange('kitchen_category', e.target.value)}
                className={kitchenFormErrors.kitchen_category ? 'error' : ''}
              >
                <option value="">Select category</option>
                <option value="Vegetarian">Vegetarian</option>
                <option value="Non-Vegetarian">Non-Vegetarian</option>
                <option value="Vegan">Vegan</option>
                <option value="Mixed">Mixed</option>
                <option value="Specialty">Specialty</option>
              </select>
              {kitchenFormErrors.kitchen_category && <div className="error-message">{kitchenFormErrors.kitchen_category}</div>}
            </div>
          </div>

          {/* Address Information */}
          <div className="user-profile-form-section">
            <h5>Address Information</h5>
            {!isLocationDetected && (
              <div className="address-header">
                <p className="location-notice">
                  📍 Location detection is required to auto-fill country and state information.
                </p>
                <button
                  type="button"
                  onClick={handleDetectKitchenLocation}
                  disabled={isDetectingKitchenLocation}
                  className="detect-address-btn"
                >
                  {isDetectingKitchenLocation ? (
                    <>
                      <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      <span>Detecting Location...</span>
                    </>
                  ) : (
                    <>
                      <MapPin size={16} />
                      <span>Detect My Location</span>
                    </>
                  )}
                </button>
              </div>
            )}
            
            {isLocationDetected && (
              <div className="location-detected-notice">
                <div className="location-success-icon">✅</div>
                <span>Location detected successfully! Country and state are auto-filled.</span>
              </div>
            )}
            
            <div className="user-profile-form-group">
              <label htmlFor="kitchen_address">Kitchen Address *</label>
              <textarea
                id="kitchen_address"
                value={kitchenFormData.kitchen_address}
                onChange={(e) => handleKitchenFormChange('kitchen_address', e.target.value)}
                className={kitchenFormErrors.kitchen_address ? 'error' : ''}
                placeholder="Enter your complete kitchen address"
                rows={3}
              />
              {kitchenFormErrors.kitchen_address && <div className="error-message">{kitchenFormErrors.kitchen_address}</div>}
            </div>

            <div className="user-profile-form-row">
              <div className="user-profile-form-group">
                <label htmlFor="city">City *</label>
                <input
                  id="city"
                  type="text"
                  value={kitchenFormData.city}
                  onChange={(e) => handleKitchenFormChange('city', e.target.value)}
                  className={kitchenFormErrors.city ? 'error' : ''}
                  placeholder="Enter city"
                />
                {kitchenFormErrors.city && <div className="error-message">{kitchenFormErrors.city}</div>}
              </div>

              <div className="user-profile-form-group">
                <label htmlFor="pincode">Pincode *</label>
                <input
                  id="pincode"
                  type="text"
                  value={kitchenFormData.pincode}
                  onChange={(e) => handleKitchenFormChange('pincode', e.target.value)}
                  className={kitchenFormErrors.pincode ? 'error' : ''}
                  placeholder="6-digit pincode"
                  maxLength={6}
                />
                {kitchenFormErrors.pincode && <div className="error-message">{kitchenFormErrors.pincode}</div>}
              </div>
            </div>

            {/* Non-editable Country and State Fields */}
            <div className="user-profile-form-row">
              <div className="user-profile-form-group">
                <label htmlFor="country">Country (Auto-detected)</label>
                <div className="auto-detected-field">
                  <input
                    id="country"
                    type="text"
                    value={kitchenFormData.country}
                    className="non-editable-field"
                    placeholder={isLocationDetected ? "Country will be auto-detected" : "Detecting..."}
                    readOnly
                  />
                  <div className="auto-detect-badge">
                    <MapPin size={14} />
                    <span>Auto</span>
                  </div>
                </div>
                {kitchenFormErrors.country && <div className="error-message">{kitchenFormErrors.country}</div>}
              </div>

              <div className="user-profile-form-group">
                <label htmlFor="state">State (Auto-detected)</label>
                <div className="auto-detected-field">
                  <input
                    id="state"
                    type="text"
                    value={kitchenFormData.state}
                    className="non-editable-field"
                    placeholder={isLocationDetected ? "State will be auto-detected" : "Detecting..."}
                    readOnly
                  />
                  <div className="auto-detect-badge">
                    <MapPin size={14} />
                    <span>Auto</span>
                  </div>
                </div>
                {kitchenFormErrors.state && <div className="error-message">{kitchenFormErrors.state}</div>}
              </div>
            </div>
          </div>

          {/* Kitchen Photo */}
          <div className="user-profile-form-section">
            <h5>Kitchen Photo</h5>
            <p className="section-description">Upload a photo of your kitchen (optional)</p>
            
            <div className="user-profile-kitchen-image-upload">
              <input
                type="file"
                id="kitchen_photo"
                accept="image/*"
                onChange={(e) => e.target.files?.[0] && handleKitchenImageUpload(e.target.files[0])}
                className="user-profile-hidden-input"
              />
              <label htmlFor="kitchen_photo" className="user-profile-kitchen-upload-button">
                <Upload size={20} />
                {kitchenFormData.kitchen_photo ? 'Change Photo' : 'Upload Photo'}
              </label>
              
              {kitchenImagePreview && (
                <div className="user-profile-kitchen-image-preview">
                  <img src={kitchenImagePreview} alt="Kitchen preview" />
                </div>
              )}
            </div>
          </div>

          {kitchenFormErrors.general && <div className="error-message general-error">{kitchenFormErrors.general}</div>}

          <div className="user-profile-form-actions">
            <button type="button" onClick={handleCancelKitchenCreate} className="user-profile-cancel-btn">
              <X size={16} />
              Cancel
            </button>
            <button type="submit" disabled={isSubmittingKitchen || !isLocationDetected} className="user-profile-save-btn">
              <Save size={16} />
              {isSubmittingKitchen ? 'Creating...' : 'Create Kitchen'}
            </button>
          </div>
        </form>
      </div>
    );
  }
  
  // Kitchen edit form
  if (isEditingKitchen) {
    return (
      <div className="user-profile-kitchen-form">
        <div className="user-profile-kitchen-form-header">
          <h4>Edit Kitchen</h4>
          <p>Update your kitchen information</p>
        </div>

        <form onSubmit={handleKitchenFormSubmit}>
          {/* Basic Information */}
          <div className="user-profile-form-section">
            <h5>Basic Information</h5>
            <div className="user-profile-form-row">
              <div className="user-profile-form-group">
                <label htmlFor="kitchen_name">Kitchen Name *</label>
                <input
                  id="kitchen_name"
                  type="text"
                  value={kitchenFormData.kitchen_name}
                  onChange={(e) => handleKitchenFormChange('kitchen_name', e.target.value)}
                  className={kitchenFormErrors.kitchen_name ? 'error' : ''}
                  placeholder="Enter kitchen name"
                />
                {kitchenFormErrors.kitchen_name && <div className="error-message">{kitchenFormErrors.kitchen_name}</div>}
              </div>

              <div className="user-profile-form-group">
                <label htmlFor="kitchen_type">Kitchen Type *</label>
                <select
                  id="kitchen_type"
                  value={kitchenFormData.kitchen_type}
                  onChange={(e) => handleKitchenFormChange('kitchen_type', e.target.value)}
                  className={kitchenFormErrors.kitchen_type ? 'error' : ''}
                >
                  <option value="">Select type</option>
                  <option value="Home Kitchen">Home Kitchen</option>
                  <option value="Cloud Kitchen">Cloud Kitchen</option>
                  <option value="Restaurant">Restaurant</option>
                </select>
                {kitchenFormErrors.kitchen_type && <div className="error-message">{kitchenFormErrors.kitchen_type}</div>}
              </div>
            </div>

            <div className="user-profile-form-row">
              <div className="user-profile-form-group">
                <label htmlFor="contact_number">Contact Number *</label>
                <input
                  id="contact_number"
                  type="tel"
                  value={kitchenFormData.contact_number}
                  onChange={(e) => handleKitchenFormChange('contact_number', e.target.value)}
                  className={kitchenFormErrors.contact_number ? 'error' : ''}
                  placeholder="10-digit phone number"
                  maxLength={10}
                />
                {kitchenFormErrors.contact_number && <div className="error-message">{kitchenFormErrors.contact_number}</div>}
              </div>

              <div className="user-profile-form-group">
                <label htmlFor="made_by">Made By *</label>
                <input
                  id="made_by"
                  type="text"
                  value={kitchenFormData.made_by}
                  onChange={(e) => handleKitchenFormChange('made_by', e.target.value)}
                  className={kitchenFormErrors.made_by ? 'error' : ''}
                  placeholder="Chef/Cook name"
                />
                {kitchenFormErrors.made_by && <div className="error-message">{kitchenFormErrors.made_by}</div>}
              </div>
            </div>

            <div className="user-profile-form-group">
              <label htmlFor="kitchen_category">Kitchen Category *</label>
              <select
                id="kitchen_category"
                value={kitchenFormData.kitchen_category}
                onChange={(e) => handleKitchenFormChange('kitchen_category', e.target.value)}
                className={kitchenFormErrors.kitchen_category ? 'error' : ''}
              >
                <option value="">Select category</option>
                <option value="Vegetarian">Vegetarian</option>
                <option value="Non-Vegetarian">Non-Vegetarian</option>
                <option value="Vegan">Vegan</option>
                <option value="Mixed">Mixed</option>
                <option value="Specialty">Specialty</option>
              </select>
              {kitchenFormErrors.kitchen_category && <div className="error-message">{kitchenFormErrors.kitchen_category}</div>}
            </div>
          </div>

          {/* Address Information */}
          <div className="user-profile-form-section">
            <h5>Address Information</h5>
            {!isLocationDetected && (
              <div className="address-header">
                <p className="location-notice">
                  📍 Location detection is required to auto-fill country and state information.
                </p>
                <button
                  type="button"
                  onClick={handleDetectKitchenLocation}
                  disabled={isDetectingKitchenLocation}
                  className="detect-address-btn"
                >
                  {isDetectingKitchenLocation ? (
                    <>
                      <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      <span>Detecting Location...</span>
                    </>
                  ) : (
                    <>
                      <MapPin size={16} />
                      <span>Detect My Location</span>
                    </>
                  )}
                </button>
              </div>
            )}
            
            {isLocationDetected && (
              <div className="location-detected-notice">
                <div className="location-success-icon">✅</div>
                <span>Location detected successfully! Country and state are auto-filled.</span>
              </div>
            )}
            
            <div className="user-profile-form-group">
              <label htmlFor="kitchen_address">Kitchen Address *</label>
              <textarea
                id="kitchen_address"
                value={kitchenFormData.kitchen_address}
                onChange={(e) => handleKitchenFormChange('kitchen_address', e.target.value)}
                className={kitchenFormErrors.kitchen_address ? 'error' : ''}
                placeholder="Enter your complete kitchen address"
                rows={3}
              />
              {kitchenFormErrors.kitchen_address && <div className="error-message">{kitchenFormErrors.kitchen_address}</div>}
            </div>

            <div className="user-profile-form-row">
              <div className="user-profile-form-group">
                <label htmlFor="city">City *</label>
                <input
                  id="city"
                  type="text"
                  value={kitchenFormData.city}
                  onChange={(e) => handleKitchenFormChange('city', e.target.value)}
                  className={kitchenFormErrors.city ? 'error' : ''}
                  placeholder="Enter city"
                />
                {kitchenFormErrors.city && <div className="error-message">{kitchenFormErrors.city}</div>}
              </div>

              <div className="user-profile-form-group">
                <label htmlFor="pincode">Pincode *</label>
                <input
                  id="pincode"
                  type="text"
                  value={kitchenFormData.pincode}
                  onChange={(e) => handleKitchenFormChange('pincode', e.target.value)}
                  className={kitchenFormErrors.pincode ? 'error' : ''}
                  placeholder="6-digit pincode"
                  maxLength={6}
                />
                {kitchenFormErrors.pincode && <div className="error-message">{kitchenFormErrors.pincode}</div>}
              </div>
            </div>

            {/* Non-editable Country and State Fields */}
            <div className="user-profile-form-row">
              <div className="user-profile-form-group">
                <label htmlFor="country">Country (Auto-detected)</label>
                <div className="auto-detected-field">
                  <input
                    id="country"
                    type="text"
                    value={kitchenFormData.country}
                    className="non-editable-field"
                    placeholder={isLocationDetected ? "Country will be auto-detected" : "Detecting..."}
                    readOnly
                  />
                  <div className="auto-detect-badge">
                    <MapPin size={14} />
                    <span>Auto</span>
                  </div>
                </div>
                {kitchenFormErrors.country && <div className="error-message">{kitchenFormErrors.country}</div>}
              </div>

              <div className="user-profile-form-group">
                <label htmlFor="state">State (Auto-detected)</label>
                <div className="auto-detected-field">
                  <input
                    id="state"
                    type="text"
                    value={kitchenFormData.state}
                    className="non-editable-field"
                    placeholder={isLocationDetected ? "State will be auto-detected" : "Detecting..."}
                    readOnly
                  />
                  <div className="auto-detect-badge">
                    <MapPin size={14} />
                    <span>Auto</span>
                  </div>
                </div>
                {kitchenFormErrors.state && <div className="error-message">{kitchenFormErrors.state}</div>}
              </div>
            </div>
          </div>

          {/* Kitchen Photo */}
          <div className="user-profile-form-section">
            <h5>Kitchen Photo</h5>
            <p className="section-description">Upload a new photo (optional)</p>
            
            <div className="user-profile-kitchen-image-upload">
              <input
                type="file"
                id="kitchen_photo"
                accept="image/*"
                onChange={(e) => e.target.files?.[0] && handleKitchenImageUpload(e.target.files[0])}
                className="user-profile-hidden-input"
              />
              <label htmlFor="kitchen_photo" className="user-profile-kitchen-upload-button">
                <Upload size={20} />
                {kitchenFormData.kitchen_photo ? 'Change Photo' : 'Upload New Photo'}
              </label>
              
              {kitchenImagePreview && (
                <div className="user-profile-kitchen-image-preview">
                  <img src={kitchenImagePreview} alt="Kitchen preview" />
                </div>
              )}
            </div>
          </div>

          {kitchenFormErrors.general && <div className="error-message general-error">{kitchenFormErrors.general}</div>}

          <div className="user-profile-form-actions">
            <button type="button" onClick={handleCancelKitchenEdit} className="user-profile-cancel-btn">
              <X size={16} />
              Cancel
            </button>
            <button type="submit" disabled={isSubmittingKitchen || !isLocationDetected} className="user-profile-save-btn">
              <Save size={16} />
              {isSubmittingKitchen ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // No kitchen state
  if (!kitchen) {
    return (
      <div className="user-profile-empty-state">
        <Building2 size={60} />
        <p>No kitchen found. Create a kitchen to start selling food.</p>
        <button onClick={handleCreateKitchen} className="user-profile-create-kitchen-btn">
          <Plus size={20} />
          Create Kitchen
        </button>
      </div>
    );
  }

  // Kitchen display
  return (
    <>
      <div className="user-profile-kitchen-display">
        <div className="user-profile-kitchen-card">
          <div className="user-profile-kitchen-image">
            {kitchen.kitchen_photo ? (
              <img src={kitchen.kitchen_photo} alt={kitchen.kitchen_name} />
            ) : (
              <div className="user-profile-kitchen-image-placeholder">
                <Building2 size={40} />
                <span>No Image</span>
              </div>
            )}
          </div>
          
          <div className="user-profile-kitchen-info">
            <h4>
              {kitchen.kitchen_name}
              <span className={`kitchen-status ${kitchen.status ? 'active' : 'inactive'}`}>
                {kitchen.status ? '🟢 ONLINE' : '🔴 OFFLINE'}
              </span>
            </h4>
            <p><strong>Type:</strong> {kitchen.kitchen_type}</p>
            <p><strong>Category:</strong> {kitchen.kitchen_category}</p>
            <p><strong>Made by:</strong> {kitchen.made_by}</p>
            <p><strong>Contact:</strong> {kitchen.contact_number}</p>
            <p><strong>Address:</strong> {kitchen.kitchen_address}, {kitchen.city}, {kitchen.state}, {kitchen.country} - {kitchen.pincode}</p>
            
            <div className="user-profile-edit-actions">
              <button 
                onClick={handleEditKitchen}
                className="user-profile-edit-btn"
              >
                <Edit size={16} />
                Edit Kitchen
              </button>
              <button 
                onClick={handleToggleKitchenStatus}
                disabled={isTogglingStatus}
                className={`user-profile-edit-btn ${kitchen.status ? 'status-active' : 'status-inactive'}`}
              >
                {isTogglingStatus ? (
                  <>
                    <div className="user-profile-loading-spinner" style={{ width: '16px', height: '16px' }}></div>
                    Updating...
                  </>
                ) : (
                  <>
                    {kitchen.status ? '🔴 Go Offline' : '🟢 Go Online'}
                  </>
                )}
              </button>
              <button 
                onClick={() => setShowDeleteKitchenConfirmation(true)}
                className="user-profile-edit-btn"
                style={{ background: '#dc3545', color: 'white' }}
              >
                <Trash2 size={16} />
                Delete Kitchen
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Kitchen Confirmation Modal */}
      {showDeleteKitchenConfirmation && (
        <div className="user-profile-confirm-dialog-overlay">
          <div className="user-profile-delete-modal">
            <div className="user-profile-delete-modal-header">
              <div className="user-profile-delete-modal-icon">
                <AlertTriangle size={24} />
              </div>
              <h3 className="user-profile-delete-modal-title">Delete Kitchen</h3>
            </div>
            
            <div className="user-profile-delete-modal-content">
              <p className="user-profile-delete-modal-text">
                Are you sure you want to delete your kitchen "{kitchen?.kitchen_name}"? This action cannot be undone.
              </p>
              
              <div className="user-profile-delete-consequences-list">
                <h4 className="user-profile-delete-consequences-title">What will be deleted:</h4>
                <div className="user-profile-delete-consequences-items">
                  <div className="user-profile-delete-consequence-item">Kitchen information</div>
                  <div className="user-profile-delete-consequence-item">Kitchen photos</div>
                  <div className="user-profile-delete-consequence-item">All food listings</div>
                  <div className="user-profile-delete-consequence-item">Order history</div>
                  <div className="user-profile-delete-consequence-item">Customer reviews</div>
                  <div className="user-profile-delete-consequence-item">Earnings data</div>
                </div>
              </div>
              
              <div className="user-profile-delete-final-warning">
                <p className="user-profile-delete-final-warning-text">
                  ⚠️ This will permanently remove your kitchen and all associated data
                </p>
              </div>
            </div>
            
            <div className="user-profile-delete-modal-actions">
              <button 
                onClick={() => setShowDeleteKitchenConfirmation(false)}
                className="user-profile-delete-cancel-btn"
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteKitchen}
                disabled={isDeletingKitchen}
                className="user-profile-delete-confirm-btn"
              >
                {isDeletingKitchen ? (
                  <>
                    <div className="user-profile-delete-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete Kitchen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserKitchenManager;